/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
/**
 * Tab Parser Utility
 *
 * Converts ASCII guitar tablature into structured TabSequence data
 * that can be animated on the fretboard.
 */

import {
  TabPosition,
  TabSequence,
  TabParseResult,
  TabParserConfig,
  DEFAULT_TAB_PARSER_CONFIG
} from '../types/tabAnimation';

/**
 * Represents a single line of tab notation
 */
interface TabLine {
  string: number;  // 0 = high E, 5 = low E
  content: string; // The part after the "|"
  stringName: string; // Original string indicator (e, B, G, etc.)
}

/**
 * Maps string indicators to string numbers for standard tuning
 */
const STRING_MAP: Record<string, number> = {
  'e': 0,  // High E (1st string)
  'b': 1,  // B (2nd string)
  'B': 1,
  'g': 2,  // G (3rd string)
  'G': 2,
  'd': 3,  // D (4th string)
  'D': 3,
  'a': 4,  // A (5th string)
  'A': 4,
  'E': 5   // Low E (6th string)
};

/**
 * Main parser function - converts ASCII tab to TabSequence
 */
export function parseTab(
  tabText: string,
  config: TabParserConfig = DEFAULT_TAB_PARSER_CONFIG
): TabParseResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  try {
    // Step 1: Split into lines and identify tab lines
    const lines = tabText.split('\n');
    const tabLines = extractTabLines(lines, errors);

    if (tabLines.length === 0) {
      errors.push('No valid tab lines found in input');
      return {
        sequence: null,
        errors,
        warnings,
        originalTab: tabText
      };
    }

    // Step 2: Extract positions from each column
    const positions = extractPositions(tabLines, config, warnings);

    if (positions.length === 0) {
      errors.push('No playable notes found in tab');
      return {
        sequence: null,
        errors,
        warnings,
        originalTab: tabText
      };
    }

    // Step 3: Sort positions by timing
    positions.sort((a, b) => a.timing - b.timing);

    // Step 4: Calculate total duration
    const totalBeats = positions.length > 0
      ? Math.max(...positions.map(p => p.timing + p.duration))
      : 0;

    // Step 5: Create the sequence
    const sequence: TabSequence = {
      positions,
      tempo: config.defaultTempo,
      totalBeats,
      stringCount: Math.max(...tabLines.map(l => l.string)) + 1
    };

    return {
      sequence,
      errors,
      warnings,
      originalTab: tabText
    };

  } catch (error) {
    errors.push(`Parser error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    return {
      sequence: null,
      errors,
      warnings,
      originalTab: tabText
    };
  }
}

/**
 * Extract tab lines from raw text
 */
function extractTabLines(lines: string[], errors: string[]): TabLine[] {
  const tabLines: TabLine[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Match patterns like "e|---3---2---|" or "B |--0--1--2--|"
    const match = line.match(/^([eEbBgGdDaA])\s*\|(.*)\|?/);

    if (match) {
      const stringIndicator = match[1];
      const content = match[2];

      const stringNumber = STRING_MAP[stringIndicator];
      if (stringNumber !== undefined) {
        tabLines.push({
          string: stringNumber,
          content,
          stringName: stringIndicator
        });
      } else {
        errors.push(`Unknown string indicator: ${stringIndicator} at line ${i + 1}`);
      }
    }
  }

  return tabLines;
}

/**
 * Extract positions from tab lines by analyzing each column
 */
function extractPositions(
  tabLines: TabLine[],
  config: TabParserConfig,
  warnings: string[]
): TabPosition[] {
  const positions: TabPosition[] = [];

  // Track consumed columns to avoid double-parsing (e.g., "8b10" shouldn't parse "10" separately)
  const consumedColumns = new Set<string>(); // Format: "lineIndex:column"

  // Find the maximum line length
  const maxLength = Math.max(...tabLines.map(l => l.content.length));

  // Process each column position
  for (let col = 0; col < maxLength; col++) {
    const notesAtColumn = extractNotesAtColumn(tabLines, col, config, consumedColumns);

    // Calculate timing based on column position and spacing
    const timing = calculateTiming(col, tabLines, config);

    // Add each note found at this column
    for (const note of notesAtColumn) {
      positions.push({
        ...note,
        timing,
        column: col
      });
    }
  }

  return positions;
}

/**
 * Extract all notes at a specific column position
 */
function extractNotesAtColumn(
  tabLines: TabLine[],
  column: number,
  config: TabParserConfig,
  consumedColumns: Set<string>
): Omit<TabPosition, 'timing' | 'column'>[] {
  const notes: Omit<TabPosition, 'timing' | 'column'>[] = [];

  for (let lineIndex = 0; lineIndex < tabLines.length; lineIndex++) {
    const line = tabLines[lineIndex];
    if (column >= line.content.length) continue;

    // Skip if this column was already consumed by a previous multi-character sequence
    const columnKey = `${lineIndex}:${column}`;
    if (consumedColumns.has(columnKey)) continue;

    const char = line.content[column];

    // Check if this is a fret number
    if (/\d/.test(char)) {
      // Look ahead for multi-digit fret numbers
      let fretStr = char;
      let nextCol = column + 1;

      while (nextCol < line.content.length && /\d/.test(line.content[nextCol])) {
        fretStr += line.content[nextCol];
        // Mark this column as consumed
        consumedColumns.add(`${lineIndex}:${nextCol}`);
        nextCol++;
      }

      const fret = parseInt(fretStr, 10);

      // Check for technique indicators and target fret after the fret number
      const techniqueInfo = config.parseTechniques
        ? parseTechnique(line.content, nextCol, lineIndex, consumedColumns)
        : {};

      notes.push({
        string: line.string,
        fret,
        duration: config.dashesPerBeat > 0 ? 1 / config.dashesPerBeat : 0.25, // Default quarter note
        technique: techniqueInfo.technique,
        targetFret: techniqueInfo.targetFret
      });

      // Mark the starting column as consumed
      consumedColumns.add(columnKey);
    }
    // Check for muted note
    else if (char === 'x' || char === 'X') {
      notes.push({
        string: line.string,
        fret: -1, // -1 indicates muted
        duration: config.dashesPerBeat > 0 ? 1 / config.dashesPerBeat : 0.25
      });

      // Mark as consumed
      consumedColumns.add(columnKey);
    }
  }

  return notes;
}

/**
 * Parse technique indicator and target fret from tab
 * Returns technique type and optional target fret for slides/bends/etc
 * Example: "5s7" = slide from 5 to 7, "5h7" = hammer from 5 to 7
 */
function parseTechnique(
  content: string,
  startPos: number,
  lineIndex: number,
  consumedColumns: Set<string>
): { technique?: TabPosition['technique'], targetFret?: number } {
  if (startPos >= content.length) return {};

  const char = content[startPos].toLowerCase();
  let technique: TabPosition['technique'] | undefined;

  switch (char) {
    case 'h': technique = 'hammer'; break;
    case 'p': technique = 'pull'; break;
    case 'b': technique = 'bend'; break;
    case 's': technique = 'slide'; break;
    case '~': technique = 'vibrato'; break;
    case 'x': technique = 'mute'; break;
    default: return {};
  }

  // Mark the technique indicator column as consumed
  consumedColumns.add(`${lineIndex}:${startPos}`);

  // Try to parse target fret after the technique indicator
  // Look for digits immediately following the technique char (e.g., "s7", "h12", "b9")
  let targetFret: number | undefined;
  let targetFretStr = '';
  let pos = startPos + 1;

  while (pos < content.length && /\d/.test(content[pos])) {
    targetFretStr += content[pos];
    // Mark target fret digit columns as consumed
    consumedColumns.add(`${lineIndex}:${pos}`);
    pos++;
  }

  if (targetFretStr) {
    targetFret = parseInt(targetFretStr, 10);
  }

  return { technique, targetFret };
}

/**
 * Calculate timing for a column based on spacing
 */
function calculateTiming(
  column: number,
  tabLines: TabLine[],
  config: TabParserConfig
): number {
  if (!config.useSpacingForTiming) {
    // Simple: each column is one unit of time
    return column / config.dashesPerBeat;
  }

  // Count dashes before this position to calculate timing
  // This is a simplified approach - we count dashes from the start
  let dashCount = 0;

  // Use the first tab line as reference for spacing
  if (tabLines.length > 0) {
    const refLine = tabLines[0].content;

    for (let i = 0; i < Math.min(column, refLine.length); i++) {
      if (refLine[i] === '-') {
        dashCount++;
      }
    }
  }

  // Convert dash count to beats
  return dashCount / config.dashesPerBeat;
}

/**
 * Validates that a tab sequence is playable
 */
export function validateTabSequence(sequence: TabSequence): string[] {
  const errors: string[] = [];

  if (sequence.positions.length === 0) {
    errors.push('No positions in sequence');
  }

  if (sequence.tempo <= 0) {
    errors.push('Invalid tempo');
  }

  if (sequence.stringCount < 4 || sequence.stringCount > 8) {
    errors.push(`Invalid string count: ${sequence.stringCount}`);
  }

  // Check for invalid string numbers
  for (const pos of sequence.positions) {
    if (pos.string < 0 || pos.string >= sequence.stringCount) {
      errors.push(`Invalid string number: ${pos.string}`);
    }

    if (pos.fret < -1 || pos.fret > 24) {
      errors.push(`Invalid fret number: ${pos.fret}`);
    }
  }

  return errors;
}
